import React, { useEffect, useRef } from "react";
import "./Footer.css";

const Footer = () => {
  const sectionRef = useRef(null);
  const backgroundImage = "https://res.cloudinary.com/dbwpaspvh/image/upload/v1763249228/abstract-orange-paint-background-acrylic-texture-with-marble-pattern_1_qtxi5x.jpg";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const elements = entry.target.querySelectorAll(
            '.animate-fade-in-left, .animate-fade-in-right, .animate-fade-in-up, .animate-scale-in'
          );
          
          if (entry.isIntersecting) {
            // Animate in when scrolling down
            elements.forEach((el) => {
              el.style.animationPlayState = 'running';
              el.style.webkitAnimationPlayState = 'running';
            });
          } else {
            // Reset when scrolling up/out of view
            elements.forEach((el) => {
              el.style.animation = 'none';
              el.style.webkitAnimation = 'none';
              el.style.opacity = '0';
              
              // Force reflow
              void el.offsetWidth;
              
              // Re-apply animation
              const animationClass = el.className.match(/animate-[\w-]+/)?.[0];
              const delay = el.style.animationDelay;
              
              if (animationClass === 'animate-fade-in-left') {
                el.style.animation = `fadeInLeft 0.8s ease-out forwards`;
                el.style.webkitAnimation = `fadeInLeft 0.8s ease-out forwards`;
              } else if (animationClass === 'animate-fade-in-right') {
                el.style.animation = `fadeInRight 0.8s ease-out forwards`;
                el.style.webkitAnimation = `fadeInRight 0.8s ease-out forwards`;
              } else if (animationClass === 'animate-fade-in-up') {
                el.style.animation = `fadeInUp 0.8s ease-out forwards`;
                el.style.webkitAnimation = `fadeInUp 0.8s ease-out forwards`;
              } else if (animationClass === 'animate-scale-in') {
                el.style.animation = `scaleIn 0.8s ease-out forwards`;
                el.style.webkitAnimation = `scaleIn 0.8s ease-out forwards`;
              }
              
              el.style.animationDelay = delay;
              el.style.webkitAnimationDelay = delay;
              el.style.animationPlayState = 'paused';
              el.style.webkitAnimationPlayState = 'paused';
            });
          }
        });
      },
      { 
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <footer 
      className="footer-section"
      id="contact"
      ref={sectionRef}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="footer-overlay"></div>
      
      <div className="footer-content">
        {/* Main Footer Content */}
        <div className="footer-main">
          <div className="footer-brand animate-fade-in-left" style={{ animationDelay: '0.2s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
            <h2 className="footer-logo">FlyFi.</h2>
            <p className="footer-tagline">
              Experience the future of connectivity with our cutting-edge technology and seamless user experience.
            </p>
            
            {/* Download Buttons */}
            <div className="download-buttons">
              <a 
                href="https://flyfi.com.ng/api/get/app/device/download" 
                className="download-btn app-store"
                aria-label="Download on App Store"
              >
                <div className="btn-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div className="btn-text">
                  <span className="btn-subtitle">Download on the</span>
                  <span className="btn-title">App Store</span>
                </div>
              </a>
              
              <a 
                href="https://flyfi.com.ng/api/get/app/device/download" 
                className="download-btn play-store"
                aria-label="Download on Google Play"
              >
                <div className="btn-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M3 20.5v-17c0-.58.47-1 1-1h1.5c.53 0 1 .47 1 1v17c0 .58-.47 1-1 1H4c-.53 0-1-.47-1-1zm16.5-8.5l-4 4-4-4 4-4 4 4zm-4-9.5l-4 4-4-4 4-4 4 4zm-9.5 9.5l-4 4-4-4 4-4 4 4z"/>
                  </svg>
                </div>
                <div className="btn-text">
                  <span className="btn-subtitle">Get it on</span>
                  <span className="btn-title">Google Play</span>
                </div>
              </a>
            </div>
          </div>

          {/* Footer Links */}
          <div className="footer-links">
            <div className="link-column animate-fade-in-up" style={{ animationDelay: '0.4s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
              <h3 className="link-title">Product.</h3>
              <ul className="link-list">
                <li><a href="#">Features</a></li>
                <li><a href="#">Pricing</a></li>
                <li><a href="#">Integrations</a></li>
              </ul>
            </div>
            
            <div className="link-column animate-fade-in-up" style={{ animationDelay: '0.5s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
              <h3 className="link-title">Company.</h3>
              <ul className="link-list">
                <li><a href="#">About Us</a></li>
                <li><a href="#">Contact</a></li>
              </ul>
            </div>
            
            <div className="link-column animate-fade-in-up" style={{ animationDelay: '0.6s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
              <h3 className="link-title">Resources.</h3>
              <ul className="link-list">
                <li><a href="#">Blog</a></li>
                <li><a href="#">Documentation</a></li>
                <li><a href="#">Support</a></li>
                <li><a href="#">API</a></li>
              </ul>
            </div>
            
            <div className="link-column animate-fade-in-up" style={{ animationDelay: '0.7s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
              <h3 className="link-title">Legal.</h3>
              <ul className="link-list">
                <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li>
                <li><a href="#">Cookie Policy</a></li>
                <li><a href="#">GDPR</a></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-divider animate-scale-in" style={{ animationDelay: '0.8s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}></div>
          
          <div className="bottom-content">
            <div className="copyright animate-fade-in-left" style={{ animationDelay: '0.9s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
              <p>&copy; 2024 FlyFi. All rights reserved.</p>
            </div>
            
            <div className="social-links animate-fade-in-right" style={{ animationDelay: '1s', animationPlayState: 'paused', WebkitAnimationPlayState: 'paused' }}>
              <a href="#" aria-label="Twitter">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                </svg>
              </a>
              <a href="#" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" aria-label="GitHub">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
              <a href="#" aria-label="Instagram">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;